import React, { useState, useEffect, useRef } from 'react';
import { authenticatedGet } from '../utils/authenticatedFetch';

const DebugViewer = () => {
    const [companyNameInput, setCompanyNameInput] = useState('');
    const [selectedStore, setSelectedStore] = useState('');
    const [validationLogs, setValidationLogs] = useState([]);
    const [selectedLogFile, setSelectedLogFile] = useState('');
    const [logContent, setLogContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fileInfo, setFileInfo] = useState(null);
    const [availableStores, setAvailableStores] = useState([]);
    const [filteredStores, setFilteredStores] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const suggestionsRef = useRef(null);

    const API_BASE = '/api';

    // Fetch available stores on component mount
    useEffect(() => {
        fetchAvailableStores();
    }, []);

    // Handle click outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch all available stores with validation logs
    const fetchAvailableStores = async () => {
        try {
            const response = await authenticatedGet(`${API_BASE}/stores/debug/validation-logs`);
            if (response.ok) {
                const result = await response.json();
                if (result.success && result.stores) {
                    setAvailableStores(result.stores);
                }
            }
        } catch (error) {
            console.error('Error fetching available stores:', error);
        }
    };

    // Filter stores based on input
    useEffect(() => {
        if (companyNameInput.trim()) {
            const filtered = availableStores.filter(store =>
                store.storeName.toLowerCase().includes(companyNameInput.toLowerCase())
            );
            setFilteredStores(filtered);
            setShowSuggestions(filtered.length > 0);
        } else {
            setFilteredStores([]);
            setShowSuggestions(false);
        }
    }, [companyNameInput, availableStores]);

    // Fetch validation logs for selected store
    const fetchValidationLogs = async (storeName) => {
        if (!storeName) return;

        setLoading(true);
        setError(null);
        setLogContent('');
        setValidationLogs([]);
        setSelectedLogFile('');
        setFileInfo(null);

        try {
            const response = await authenticatedGet(`${API_BASE}/stores/debug/validation/${encodeURIComponent(storeName)}`);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();

            if (result.success) {
                // Response structure: successResponse spreads data directly into result
                if (result.allLogs && result.allLogs.length > 0) {
                    setValidationLogs(result.allLogs);
                    setSelectedLogFile(result.logFile);
                    setLogContent(result.content || '');
                    setFileInfo(result.fileInfo);
                } else {
                    setError(`No validation logs found for ${storeName}. Run a validation first to generate logs.`);
                }
            } else {
                setError(result.error || 'Unknown error');
            }
        } catch (error) {
            console.error('Error fetching validation logs:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch specific log file
    const fetchSpecificLog = async (storeName, logFileName) => {
        if (!storeName || !logFileName) return;

        setLoading(true);
        setError(null);

        try {
            const response = await authenticatedGet(
                `${API_BASE}/stores/debug/validation/${encodeURIComponent(storeName)}?logFile=${encodeURIComponent(logFileName)}`
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();

            if (result.success) {
                // Response structure: successResponse spreads data directly into result
                setLogContent(result.content || '');
                setFileInfo(result.fileInfo);
                setSelectedLogFile(logFileName);
            } else {
                setError(result.error || 'Unknown error');
            }
        } catch (error) {
            console.error('Error fetching specific log:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        const storeName = companyNameInput.trim();
        if (storeName) {
            setSelectedStore(storeName);
            setShowSuggestions(false);
            fetchValidationLogs(storeName);
        }
    };

    // Handle suggestion click
    const handleSuggestionClick = (storeName) => {
        setCompanyNameInput(storeName);
        setSelectedStore(storeName);
        setShowSuggestions(false);
        fetchValidationLogs(storeName);
    };

    // Handle clear
    const handleClear = () => {
        setCompanyNameInput('');
        setSelectedStore('');
        setValidationLogs([]);
        setLogContent('');
        setSelectedLogFile('');
        setFileInfo(null);
        setError(null);
        setShowSuggestions(false);
    };

    // Handle log file selection
    const handleLogFileChange = (e) => {
        const logFileName = e.target.value;
        if (logFileName && selectedStore) {
            fetchSpecificLog(selectedStore, logFileName);
        }
    };

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    // Format file size
    const formatFileSize = (sizeKB) => {
        if (sizeKB < 1024) {
            return `${sizeKB} KB`;
        }
        return `${(sizeKB / 1024).toFixed(2)} MB`;
    };

    return (
        <div className="min-h-screen bg-gray-100 py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="bg-white shadow rounded-lg mb-6 p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-3xl font-bold text-gray-900">Validation Logs Viewer</h1>
                        <div className="text-sm text-gray-500">
                            Unified validation logs from ValidationLogger
                        </div>
                    </div>

                    {/* Store Name Input with Autocomplete */}
                    <form onSubmit={handleSubmit} className="mb-4">
                        <label htmlFor="company-name" className="block text-sm font-medium text-gray-700 mb-2">
                            Enter Store/Company Name
                            {availableStores.length > 0 && (
                                <span className="ml-2 text-xs text-gray-500">
                                    ({availableStores.length} stores with logs)
                                </span>
                            )}
                        </label>
                        <div className="relative">
                            <div className="flex gap-2">
                                <div className="flex-1 relative">
                                    <input
                                        id="company-name"
                                        type="text"
                                        value={companyNameInput}
                                        onChange={(e) => setCompanyNameInput(e.target.value)}
                                        onFocus={() => {
                                            if (filteredStores.length > 0) {
                                                setShowSuggestions(true);
                                            }
                                        }}
                                        placeholder="Start typing to search..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        autoComplete="off"
                                    />
                                    {showSuggestions && filteredStores.length > 0 && (
                                        <div
                                            ref={suggestionsRef}
                                            className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
                                        >
                                            {filteredStores.map((store, index) => (
                                                <div
                                                    key={index}
                                                    onClick={() => handleSuggestionClick(store.storeName)}
                                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-medium text-gray-900">{store.storeName}</span>
                                                        <span className="text-xs text-gray-500">
                                                            {store.logCount} log{store.logCount !== 1 ? 's' : ''}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Last updated: {new Date(store.latestModified).toLocaleString()}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    disabled={!companyNameInput.trim() || loading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Load Logs
                                </button>
                                {selectedStore && (
                                    <button
                                        type="button"
                                        onClick={handleClear}
                                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                        {selectedStore && (
                            <div className="mt-2 text-sm text-gray-600">
                                Viewing logs for: <span className="font-medium text-gray-900">{selectedStore}</span>
                            </div>
                        )}
                    </form>

                    {/* Log File Selection */}
                    {validationLogs.length > 0 && (
                        <div className="mb-4">
                            <label htmlFor="log-select" className="block text-sm font-medium text-gray-700 mb-2">
                                Select Log File (Last {validationLogs.length} validations)
                            </label>
                            <select
                                id="log-select"
                                value={selectedLogFile}
                                onChange={handleLogFileChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                {validationLogs.map((log) => (
                                    <option key={log.filename} value={log.filename}>
                                        {formatTimestamp(log.modified)} - {formatFileSize(log.sizeKB)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* File Info */}
                    {fileInfo && (
                        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <span className="font-medium text-blue-900">File:</span>{' '}
                                    <span className="text-blue-700">{selectedLogFile}</span>
                                </div>
                                <div>
                                    <span className="font-medium text-blue-900">Size:</span>{' '}
                                    <span className="text-blue-700">{formatFileSize(fileInfo.sizeKB)}</span>
                                </div>
                                <div>
                                    <span className="font-medium text-blue-900">Modified:</span>{' '}
                                    <span className="text-blue-700">{formatTimestamp(fileInfo.modified)}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error Display */}
                    {error && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                            <strong>Error:</strong> {error}
                        </div>
                    )}
                </div>

                {/* Log Content Display */}
                <div className="bg-white shadow rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-medium text-gray-900">
                            Validation Log Content
                        </h2>
                    </div>

                    <div className="p-6">
                        {loading && (
                            <div className="text-center text-gray-500 py-8">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                                <div>Loading validation logs...</div>
                            </div>
                        )}

                        {!loading && !selectedStore && (
                            <div className="text-center text-gray-500 py-8">
                                Enter a store/company name above to view validation logs
                            </div>
                        )}

                        {!loading && selectedStore && !logContent && !error && (
                            <div className="text-center text-gray-500 py-8">
                                No validation logs available for this store
                            </div>
                        )}

                        {!loading && logContent && (
                            <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                                <pre className="text-xs font-mono whitespace-pre-wrap">{logContent}</pre>
                            </div>
                        )}
                    </div>
                </div>

                {/* Help Section */}
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-900 mb-2">ℹ️ About Validation Logs</h3>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                        <li>Logs are stored in <code className="bg-blue-100 px-1 rounded">backend/validation-logs/</code></li>
                        <li>Each validation creates a single comprehensive log file</li>
                        <li>Only the last 3 validations per store are kept (automatic rotation)</li>
                        <li>Logs include complete validation details from start to finish</li>
                        <li>Maximum log size is 5MB per file with auto-truncation</li>
                        <li>Start typing a store name to see available logs with autocomplete</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default DebugViewer;
